// src/resources/Letters/pdfHelper.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * handleDownloadPDF
 * Safely generates PDF for both Arabic and English letters.
 * Prevents black-screen PDFs by enforcing correct CORS mode.
 */
export const handleDownloadPDF = async (options) => {
  try {
    if (options && options.preventDefault) {
      options.preventDefault();
      return;
    }

    const { direction, previewRef, referenceNo } = options;

    if (!previewRef?.current) {
      alert("⚠️ Please ensure the letter preview is visible.");
      return;
    }

    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();

    // --- CLONE NODE SAFELY ---
    const clone = previewRef.current.cloneNode(true);
    document.body.appendChild(clone);

    clone.style.position = "absolute";
    clone.style.top = "-20000px";
    clone.style.left = "0";
    clone.style.width = `${previewRef.current.offsetWidth}px`;
    clone.style.height = `${previewRef.current.offsetHeight}px`;
    clone.style.background = "#ffffff";
    clone.style.zIndex = "-9999";

    // --- Arabic Fixes ---
    if (direction === "rtl") {
      clone.style.direction = "rtl";
      clone.style.textAlign = "right";
      clone.style.fontFamily = "'Amiri','Noto Naskh Arabic',serif";
    }

    // Force Quill alignments
    clone.querySelectorAll(".ql-align-center").forEach(
      (el) => (el.style.textAlign = "center")
    );
    clone.querySelectorAll(".ql-align-right").forEach(
      (el) => (el.style.textAlign = "right")
    );
    clone.querySelectorAll(".ql-align-justify").forEach(
      (el) => (el.style.textAlign = "justify")
    );

    // --- WAIT for images to fully load ---
    await Promise.all(
      Array.from(clone.querySelectorAll("img")).map((img) => {
        return new Promise((resolve) => {
          img.crossOrigin = "anonymous";
          if (img.complete) resolve();
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    // --- html2canvas FIXED CONFIG ---
    const canvas = await html2canvas(clone, {
      scale: 3,
      useCORS: true,
      allowTaint: false,      // 🔥 MUST be FALSE to avoid black PDF
      backgroundColor: "#ffffff",
      removeContainer: true,
      imageTimeout: 5000,
      logging: false,
      onclone: (clonedDoc) => {
        // Ensure letterhead images use crossOrigin=anonymous
        clonedDoc
          .querySelectorAll("img")
          .forEach((i) => (i.crossOrigin = "anonymous"));
      },
    });

    document.body.removeChild(clone);

    // --- ADD TO PDF ---
    const imgData = canvas.toDataURL("image/png", 1.0);
    const width = pageWidth;
    const height = (canvas.height * width) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, width, height);

    // --- SAVE FILE ---
    const filename =
      (referenceNo || "Letter") +
      "_" +
      (direction === "rtl" ? "Arabic" : "English") +
      ".pdf";

    pdf.save(filename);
  } catch (err) {
    console.error("❌ PDF generation failed:", err);
    alert("Error creating PDF: " + err.message);
  }
};
